/**
 * Frontend permissions — mirrors backend `permissions.py`.
 *
 * Usage:
 *   import { usePermissions, P } from '@/lib/permissions';
 *   const { can, isWhitelabel } = usePermissions();
 *   if (!can(P.VIEW_AUDIT)) return null;
 *
 * Source of truth is the backend (which also enforces server-side). This
 * hook caches the permission set in memory + sessionStorage so the navbar
 * paints instantly on subsequent renders.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

// Mirror of backend permissions.P — keep in sync.
export const P = {
  VIEW_OVERVIEW: 'view_overview',
  VIEW_APPROVALS: 'view_approvals',
  VIEW_AGREEMENTS: 'view_agreements',
  VIEW_AGREEMENTS_DRAFT: 'view_agreements_draft',
  VIEW_ENTITIES: 'view_entities',
  VIEW_ENTITIES_LIVE: 'view_entities_live',
  VIEW_ENTITY_DETAIL: 'view_entity_detail',
  VIEW_STARTUPS: 'view_startups',
  VIEW_INVESTORS: 'view_investors',
  VIEW_OPPORTUNITIES: 'view_opportunities',
  VIEW_FOUNDER_JOURNEY: 'view_founder_journey',
  VIEW_MINUTES: 'view_minutes',
  VIEW_MEETINGS: 'view_meetings',
  VIEW_EVENTS: 'view_events',
  VIEW_CHAT: 'view_chat',
  VIEW_HELP: 'view_help',
  VIEW_ERM: 'view_erm',
  VIEW_DOCUMENTS: 'view_documents',
  UPLOAD_DOCUMENTS: 'upload_documents',
  DELETE_DOCUMENTS: 'delete_documents',
  VIEW_AI_EMPLOYEES: 'view_ai_employees',
  VIEW_AI_COUNCIL: 'view_ai_council',
  CONVENE_COUNCIL: 'convene_council',
  VIEW_AI_DRAFTS: 'view_ai_drafts',
  VIEW_AI_MENTORS: 'view_ai_mentors',
  TRAIN_AI_MENTORS: 'train_ai_mentors',
  VIEW_AUDIT: 'view_audit',
  VIEW_AUDIT_CROSS_TENANT: 'view_audit_cross_tenant',
  VIEW_SECURITY: 'view_security',
  VIEW_API_DOCS: 'view_api_docs',
  VIEW_CHANGE_REQUESTS: 'view_change_requests',
  SUBMIT_FEEDBACK: 'submit_feedback',
  VIEW_TENANT_SETTINGS: 'view_tenant_settings',
  MANAGE_TENANT_SETTINGS: 'manage_tenant_settings',
  VIEW_BANK_BILLING: 'view_bank_billing',
  MANAGE_BANK_BILLING: 'manage_bank_billing',
  MANAGE_SUBSCRIPTION: 'manage_subscription',
  MANAGE_USERS: 'manage_users',
  MANAGE_ROLES: 'manage_roles',
  IMPERSONATE_ROLES: 'impersonate_roles',
  MANAGE_ENTITIES: 'manage_entities',
  VIEW_FINANCE: 'view_finance',
  MANAGE_FINANCE: 'manage_finance',
};

const CACHE_KEY = 've_permissions_cache_v1';

function _readCache() {
  try {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function _writeCache(data) {
  try {
    if (typeof window === 'undefined') return;
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch { /* quota / private mode — ignore */ }
}

export function clearPermissionsCache() {
  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

export function usePermissions() {
  const cached = _readCache();
  const [state, setState] = useState(cached || {
    permissions: [],
    role: null,
    tenant_id: 'primary',
    is_whitelabel: false,
    loaded: false,
  });

  useEffect(() => {
    let cancelled = false;
    api.get('/auth/me/permissions')
      .then(({ data }) => {
        if (cancelled) return;
        const next = { ...data, loaded: true };
        setState(next);
        _writeCache(next);
      })
      .catch(() => {
        if (cancelled) return;
        setState((s) => ({ ...s, loaded: true }));
      });
    return () => { cancelled = true; };
  }, []);

  const can = useCallback(
    (perm) => Array.isArray(state.permissions) && state.permissions.includes(perm),
    [state.permissions],
  );

  return {
    can,
    permissions: state.permissions || [],
    role: state.role,
    tenantId: state.tenant_id || 'primary',
    isWhitelabel: !!state.is_whitelabel,
    loaded: state.loaded,
  };
}
